import { alertController } from '@ionic/core';
import { Component, forceUpdate, Fragment, h, Prop, State } from '@stencil/core';
import { PersonDoc, ProjectDoc, Workflow, WorkflowState } from 'common';
import { SubmittalUtils } from '..';
import { ConcourseDb, PeopleService, User } from '../../global';
import { ProjectService } from '../../projects/services/ProjectService';
import { RequestAccess } from '../../components/concourse-login/share-link/request-access';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { WorkflowEditorModal } from './workflow-editor-modal';

@Component({
  tag: 'concourse-submittals-settings',
  styleUrl: 'concourse-submittals.css',
  //shadow: true,
})
export class ConcourseSubmittalsSettings {
  @Prop() projectId: string;
  @State() workflows: WorkflowState[];
  @State() editIndex: number = -1;
  @State() columns: string[] = [];
  @State() visibleColumns: string[] = [];

  private project: ProjectDoc;
  private people: PersonDoc[];

  get user(): User {
    return globalThis.user;
  }

  async componentWillLoad() {
    const permissions = this.user.getSubmittalsPermissions(this.projectId);
    if (!permissions.canWrite) {
      return RequestAccess.presentModal(this.projectId, 'Submittals', permissions.writeRole);
    }
    this.project = await ProjectService.getProjectDoc(this.projectId);
    this.people = await PeopleService.getPersonDocs(this.projectId);

    this.columns = this.project.submittal_columns || [];

    // Initialize visible columns - use project settings or default to all columns
    this.visibleColumns = this.project.submittal_columns || SubmittalUtils.TABLE_COLUMNS.map(col => col.key);
  }

  async chooseManager() {
    const people = await ConcoursePersonSelector.present({ projectId: this.projectId, feature: 'submittals', needsWrite: true });
    if (people && people.length > 0) {
      this.project.submittals_manager = people[0]._id;
      this.project = await ProjectService.saveProjectDoc(this.project);
      forceUpdate(this);
    }
  }

  async duplicateDefaultWorkflow() {
    this.editWorkflow({ id: ConcourseDb.makeShortDocId(), name: null, states: [...SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW.states] });
  }

  async editWorkflow(workflow: Workflow) {
    const edited = await WorkflowEditorModal.present(this.projectId, workflow, 'submittals');
    const workflows = this.project.submittals_workflows || [];
    if (edited && (edited as Workflow).id) {
      const idx = workflows.findIndex(w => w.id == (edited as Workflow).id);
      if (idx > -1) {
        workflows[idx] = edited as Workflow;
      } else {
        workflows.push(edited as Workflow);
      }
      this.project = await ProjectService.saveProjectDoc({ ...this.project, submittals_workflows: workflows });
    } else if (edited == 'delete') {
      this.project = await ProjectService.saveProjectDoc({ ...this.project, submittals_workflows: workflows.filter(w => w.id != workflow.id) });
    }
    forceUpdate(this);
  }

  async addNewPackage() {
    const alert = await alertController.create({
      header: 'Enter Package Name',
      inputs: [
        {
          name: 'packageName',
          type: 'text',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'OK',
          handler: async data => {
            if (data.packageName) {
              // Add to project's submittal packages
              if (this.project) {
                this.project.submittals_packages = this.project.submittals_packages || [];
                this.project.submittals_packages.push({ title: data.packageName });
                await ProjectService.saveProjectDoc(this.project);
              }

              // Force update to show the new package in the dropdown
              forceUpdate(this);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async deletePackage(index: number) {
    const alert = await alertController.create({
      header: 'Delete Package',
      message: `Are you sure you want to delete "${this.project.submittals_packages[index].title}"?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.project.submittals_packages.splice(index, 1);
            await ProjectService.saveProjectDoc(this.project);
            forceUpdate(this);
          },
        },
      ],
    });
    alert.present();
  }

  async addNewSubmittalType() {
    const alert = await alertController.create({
      header: 'Enter Submittal Type',
      inputs: [
        {
          name: 'typeName',
          type: 'text',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'OK',
          handler: async data => {
            if (data.typeName) {
              // Initialize with defaults if this is the first modification
              if (!this.project.submittals_types || this.project.submittals_types.length === 0) {
                this.project.submittals_types = [...SubmittalUtils.DEFAULT_SUBMITTAL_TYPES];
              }
              this.project.submittals_types.push(data.typeName);
              await ProjectService.saveProjectDoc(this.project);

              // Force update to show the new type
              forceUpdate(this);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async deleteSubmittalType(index: number) {
    // Get the current types (either from project or defaults)
    const currentTypes = this.project.submittals_types || SubmittalUtils.DEFAULT_SUBMITTAL_TYPES;
    const typeToDelete = currentTypes[index];

    const alert = await alertController.create({
      header: 'Delete Submittal Type',
      message: `Are you sure you want to delete "${typeToDelete}"?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            // Initialize with defaults if this is the first modification
            if (!this.project.submittals_types) {
              this.project.submittals_types = [...SubmittalUtils.DEFAULT_SUBMITTAL_TYPES];
            }
            this.project.submittals_types.splice(index, 1);
            await ProjectService.saveProjectDoc(this.project);
            forceUpdate(this);
          },
        },
      ],
    });
    alert.present();
  }

  async toggleColumnVisibility(columnKey: string) {
    const isVisible = this.visibleColumns.includes(columnKey);

    if (isVisible) {
      // Remove column from visible list
      this.visibleColumns = this.visibleColumns.filter(col => col !== columnKey);
    } else {
      // Add column to visible list
      this.visibleColumns = [...this.visibleColumns, columnKey];
    }

    // Save to project
    this.project.submittal_columns = [...this.visibleColumns];
    await ProjectService.saveProjectDoc(this.project);

    forceUpdate(this);
  }

  async resetToDefaultColumns() {
    this.visibleColumns = [...SubmittalUtils.TABLE_COLUMNS.map(col => col.key)];
    this.project.submittal_columns = null;
    await ProjectService.saveProjectDoc(this.project);
    forceUpdate(this);
  }

  navigateToSubmittals() {
    window.location.hash = `/project/${this.projectId}/submittals`;
  }

  render() {
    const workflows = [...(this.project.submittals_workflows || [])];
    workflows.unshift(SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW);

    // Use project types if they exist, otherwise use defaults
    const submittalTypes = this.project.submittals_types || SubmittalUtils.DEFAULT_SUBMITTAL_TYPES;

    return (
      <Fragment>
        <concourse-header
          projectId={this.projectId}
          pageTitle="Submittals Settings"
          showBack={true}
          onBack={() => this.navigateToSubmittals()}
        ></concourse-header>
        <ion-content>
          <ion-grid>
            <ion-row>
              <ion-col size-md="8" size-xs="12">
                {/* Workflows Section */}
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Workflows</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {workflows.map(workflow => (
                        <ion-item>
                          <ion-label>{workflow.name}</ion-label>
                          {workflow.id != SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW.id && (
                            <ion-button slot="end" fill="clear" onClick={() => this.editWorkflow(workflow)}>
                              <ion-icon slot="icon-only" name="create"></ion-icon>
                            </ion-button>
                          )}
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-button size="small" fill="clear" class="link-button" onClick={() => this.duplicateDefaultWorkflow()}>
                      <ion-icon slot="start" name="add-outline"></ion-icon>
                      New Workflow
                    </ion-button>
                  </ion-card-content>
                </ion-card>

                {/* Submittal Types Section */}
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Submittal Types</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {submittalTypes.map((type, index) => (
                        <ion-item>
                          <ion-label>{type}</ion-label>
                          <ion-button slot="end" fill="clear" color="danger" onClick={() => this.deleteSubmittalType(index)}>
                            <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                          </ion-button>
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-button size="small" fill="clear" class="link-button" onClick={() => this.addNewSubmittalType()}>
                      <ion-icon slot="start" name="add-outline"></ion-icon>
                      Add Submittal Type
                    </ion-button>
                  </ion-card-content>
                </ion-card>

                {/* Packages Section */}
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Packages</ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {(this.project.submittals_packages || []).map((pkg, index) => (
                        <ion-item>
                          <ion-label>{pkg.title}</ion-label>
                          <ion-button slot="end" fill="clear" color="danger" onClick={() => this.deletePackage(index)}>
                            <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                          </ion-button>
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-button size="small" fill="clear" class="link-button" onClick={() => this.addNewPackage()}>
                      <ion-icon slot="start" name="add-outline"></ion-icon>
                      Add Package
                    </ion-button>
                  </ion-card-content>
                </ion-card>
              </ion-col>

              <ion-col size-md="4" size-xs="12">
                {/* Column Visibility Section */}
                <ion-card>
                  <ion-card-header>
                    <ion-card-title>Table Columns</ion-card-title>
                    <ion-card-subtitle>Choose which columns to display in the submittals table</ion-card-subtitle>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-list>
                      {SubmittalUtils.TABLE_COLUMNS.map(column => (
                        <ion-item>
                          <ion-checkbox slot="start" checked={this.visibleColumns.includes(column.key)} onIonChange={() => this.toggleColumnVisibility(column.key)}></ion-checkbox>
                          <ion-label>{column.label}</ion-label>
                        </ion-item>
                      ))}
                    </ion-list>
                    <ion-button size="small" fill="clear" class="link-button" onClick={() => this.resetToDefaultColumns()}>
                      <ion-icon slot="start" name="refresh-outline"></ion-icon>
                      Reset to Default
                    </ion-button>
                  </ion-card-content>
                </ion-card>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ion-content>
      </Fragment>
    );
  }
}
